<template>
  <div class="reader-wrapper" v-loading="loading">
    <div ref="viewContainer" class="reader-body">
      <component v-if="pageView" ref="pageView" :is="pageView" :eventBus="eventBus"/>
    </div>
    <div class="doc-page-tools">
      <i class="el-icon-download tools-btn" v-show="docTopVisible" @click="handleToEnd(-1)"></i>
      <i class="el-icon-download tools-btn" v-show="docBottomVisible" @click="handleToEnd(1)"></i>
    </div>
  </div>
</template>

<script>
import PDFView from "./pdf/pdf-view.vue";
import OFDView from "./ofd/ofd-view.vue";
import {EventBus} from "./util/event_utils";
import PageSealManager from "./util/page_seal_manager";
import 'pdfjs-dist/legacy/web/pdf_viewer.css'

const DEFAULT_SCALE_VALUE = 'auto'

export default {
  name: "file-reader",
  components: {
    PDFView,
    OFDView
  },
  props: {
    file: Object,
    currentPage: {
      type: Number,
      default: 1
    },
    currentScale: {
      type: Number,
      default: 0
    },
  },
  data() {
    return {
      pageView: null,
      fileViewer: null,
      internalPagesCount: 0,
      internalCurrentPage: 1,
      internalCurrentScale: 1,
      eventBus: new EventBus(),
      docTopVisible: false,
      docBottomVisible: false,
      loading: false,
      sealManager: null,
    }
  },
  mounted() {
    const eventBus = this.eventBus
    this.sealManager = new PageSealManager(eventBus, this.$refs.viewContainer)

    const self = this;
    eventBus.on("pagesinit", function (evt) {
      self.fileViewer = evt.source
      self.sealManager.setViewer(evt.source, self.file)

      if (self.currentScale > 0) {
        self.fileViewer.currentScale = self.currentScale;
      } else {
        self.fileViewer.currentScaleValue = DEFAULT_SCALE_VALUE;
      }
    });

    eventBus.on("pagesloaded", function (evt) {
      self.loading = false;

      const {pagesCount, source} = evt
      const {currentScale, currentPageNumber} = source

      self.internalPagesCount = pagesCount
      self.internalCurrentScale = currentScale
      self.internalCurrentPage = currentPageNumber
      self.docBottomVisible = currentPageNumber < pagesCount

      self.$emit("page-ready", {
        pagesCount,
        currentScale,
        currentPageNumber
      })
    });

    eventBus.on("pagechanging", function (evt) {
      self.internalCurrentPage = evt.pageNumber
      self.$emit("update:currentPage", evt.pageNumber);
      self.$emit("page-change", evt.pageNumber);
    });

    eventBus.on("scalechanging", function (evt) {
      self.internalCurrentScale = evt.scale
      self.$emit("scale-change", evt)
    });

    eventBus.on("sealremoved", function (evt) {
      self.$emit("seal-remove", evt)
    });

    eventBus.on("sealadded", function (evt) {
      self.$emit("seal-add", evt)
    });

    eventBus.on("sealchanged", function (evt) {
      self.$emit("seal-change", evt)
    });

    this.open()
  },
  watch: {
    file() {
      this.open()
    },
    currentPage(newValue) {
      this._toPage(newValue)
    },
    currentScale(newValue) {
      this._setScale(newValue)
    }
  },
  methods: {
    open() {
      const {type, src} = this.file
      if (type === "pdf") {
        this.pageView = 'PDFView'
      } else if (type === "ofd") {
        this.pageView = 'OFDView'
      } else {
        this.$alert('不支持的文档')
        return
      }

      if (this.currentFile && this.currentFile.src === src) return;
      if (this.$refs.pageView) {
        this.$refs.pageView.cancel()
      }

      this.loading = true;
      this.$nextTick(() => {
        this.$refs.pageView.load(src).then(() => {
          this.currentFile = this.file
        }).catch((err) => {
          this.loading = false;
          this.$alert('文档打开失败')
        })
      })
    },
    _setScale(scale) {
      if (scale < 0 || scale > 3 || !this.fileViewer || scale === this.internalCurrentScale) {
        return
      }

      this.internalCurrentScale = scale
      this.fileViewer.currentScale = scale
    },

    _toPage(page) {
      this.docTopVisible = page > 1
      this.docBottomVisible = page < this.internalPagesCount
      if (page < 1 || page > this.internalPagesCount || !this.fileViewer || page === this.internalCurrentPage) {
        return
      }

      this.internalCurrentPage = page
      this.fileViewer.currentPageNumber = page
    },
    handleToEnd(flag) {
      if (flag < 0) {
        this._toPage(1)
      } else {
        this._toPage(this.internalPagesCount)
      }
    },
    addSeal(seals) {
      if (!this.sealManager) return

      this.sealManager.appendSeals(seals)
    },

    hasSeals(fileId) {
      return this.sealManager && this.sealManager.hasSeals(fileId);
    },

    getFileSeals: function (fileId) {
      if (!this.sealManager) return null

      return this.sealManager.getFileSeals(fileId)
    },

    getSeals: function () {
      if (!this.sealManager) return null

      return this.sealManager.getSeals()
    },

    getInternalPagesCount:function () {
      return this.internalPagesCount
    },
  }
}
</script>

<style lang="less">
.page .seal-wrap {
  position: absolute;
  cursor: grab;
  z-index: 2;
}

.page .seal-wrap.selected {
  background-color: rgba(255, 255, 255, .4);
}

.page .seal-wrap .seal-remove {
  position: absolute;
  top: -1.2rem;
  right: -1.2rem;
  width: 3rem;
  height: 3rem;
  z-index: 3;
  display: none;
  cursor: default;
}

.page .seal-wrap.selected .seal-remove {
  display: block;
}

.page .seal-wrap .seal-img {
  width: 100%;
  height: 100%;
  display: block;
}
</style>

<style scoped lang="less">
.reader-wrapper {
  height: 100%;
  width: 100%;
  position: relative;
}

.reader-body {
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  overflow: auto;
  position: absolute;
}

.doc-page-tools {
  width: 48px;
  bottom: 48px;
  right: 0;
  position: absolute;

  .tools-btn {
    width: 32px;
    height: 32px;
    font-size: 20px;
    line-height: 32px;
    border-radius: 4px;
    display: block;
    color: #fff;
    text-decoration: none;
    background-color: rgba(146, 146, 146, 0.8);
    cursor: pointer;
    transition: all .25s ease-in-out;
    margin-top: 8px;
    user-select: none;
    text-align: center;
    border: 1px solid transparent;
  }

  .tools-btn:hover {
    background-color: rgb(146, 146, 146);
  }

  .tools-btn:active, .tools-btn:focus {
    border: 1px solid #cccccc;
  }

  .tools-btn:first-child {
    transform: rotate(-180deg);
  }
}
</style>
